// Copyright (c) Move Closer 2022.

import { MappingConfig, MappingTypes } from '@movecloser/front-core'

import { ShippingMethod } from '../../../contexts'

import { mapRegion } from '../helpers/region-mapper'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const cartItemAdapterMap: MappingConfig = {
  appliedCoupons: 'appliedCoupons',
  prices: {
    type: MappingTypes.Function,
    value: (item) => {
      return {
        price: item.prices.price,
        total: item.prices.row_total_including_tax,
        totalDiscount: item.prices.row_item_discount
      }
    }
  },
  product: 'product',
  outOfStock: {
    type: MappingTypes.Function,
    value: (item) => {
      return item.product.stock_status === 'OUT_OF_STOCK'
    }
  },
  quantity: 'quantity',
  uid: 'uid'
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const cartAdapterMap: MappingConfig = {
  id: 'id',
  coupons: {
    type: MappingTypes.Function,
    value: (item) => {
      if (!item.applied_coupons) {
        return []
      }

      return item.applied_coupons.map((coupon: { code: string }) => {
        return {
          code: coupon.code
        }
      })
    }
  },
  discounts: {
    type: MappingTypes.Function,
    value: (item) => {
      return item.prices?.discounts
    }
  },
  billingAddress: {
    type: MappingTypes.Function,
    value: (item) => {
      if (!item.billing_address) {
        return null
      }

      const { country, vat_id: vatId, region, ...rest } = item.billing_address

      const data = {
        ...rest,
        countryCode: country?.code,
        vatId
      }

      return region ? {
        ...data,
        ...mapRegion(region)
      } : data
    }
  },
  items: {
    type: MappingTypes.Adapter,
    map: cartItemAdapterMap,
    value: 'items'
  },
  note: {
    type: MappingTypes.Function,
    value: (item) => {
      if (!item.shipping_addresses || !item.shipping_addresses.length) {
        return null
      }
      return item.shipping_addresses[0].customer_notes
    }
  },
  selectedPaymentMethodId: 'selected_payment_method_id',
  selectedPaymentMethod: {
    type: MappingTypes.Function,
    value: (item) => {
      return item.selected_payment_method ?? null
    }
  },
  selectedShippingMethod: {
    type: MappingTypes.Function,
    value: (item): ShippingMethod | null => {
      if (!item.shipping_addresses || !item.shipping_addresses.length) {
        return null
      }

      const method = item.shipping_addresses[0].selected_shipping_method

      return method ? {
        carrierCode: method.carrier_code,
        deliveryTime: method.delivery_time,
        displayInfo: method.display_info,
        methodCode: method.method_code,
        price: method.amount
      } : null
    }
  },
  shippingAddress: {
    type: MappingTypes.Function,
    value: (item) => {
      if (!item.shipping_addresses || !item.shipping_addresses.length) {
        return null
      }

      const { country, region, ...rest } = item.shipping_addresses[0]

      const data = {
        ...rest,
        countryCode: country?.code
      }

      return region ? {
        ...data,
        ...mapRegion(region)
      } : data
    }
  },
  subtotal: {
    type: MappingTypes.Function,
    value: (item) => {
      return item.prices?.subtotal_including_tax
    }
  },
  subtotalExclTaxes: {
    type: MappingTypes.Function,
    value: (item) => {
      return item.prices?.subtotal_excluding_tax
    }
  },
  taxes: {
    type: MappingTypes.Function,
    value: (item) => {
      return item.prices?.applied_taxes
    }
  },
  total: {
    type: MappingTypes.Function,
    value: (item) => {
      return item.prices?.grand_total
    }
  }
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export const updatedItemsAdapterMap: MappingConfig = {
  cart_item_uid: 'uid',
  quantity: 'quantity'
}
